import React, { useMemo } from 'react'
import type { TFunction } from 'react-i18next'
import '../theme/InstallmentTimeline.css'

/**
 * Interface for installment data
 */
/* eslint-disable */
interface Installment {
  amount: string
  isCompleted?: boolean
  progressType?: 'empty' | 'small' | 'medium' | 'full'
}

interface InstallmentTimelineProps {
  price: number
  installmentAmount?: string | number
  selectedMonths: number
  t: TFunction
  locale?: string
  maxVisibleInstallments?: number
  useFirstInstallmentDays?: boolean
  firstInstallmentDays?: number
}

const InstallmentTimeline: React.FC<InstallmentTimelineProps> = ({ price, installmentAmount, selectedMonths, t, locale = 'en', maxVisibleInstallments = 3, useFirstInstallmentDays = true, firstInstallmentDays = 45 }) => {
  // Format the price with the thousands separator
  const formatPrice = (value: number | string): string => {
    return Number(value).toLocaleString('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    })
  }

  /**
   * Generate installment data based on price and selectedMonths
   */
  const generateInstallments = (): Installment[] => {
    const installments: Installment[] = []
    if (price <= 0 || selectedMonths <= 0) {
      return installments
    }

    // Use provided installment amount or calculate it
    const baseAmount = installmentAmount
      ? Number(installmentAmount)
      : Number((price / selectedMonths).toFixed(2))

    // Calculate the remainder to distribute
    const remainder = Number((price - (baseAmount * selectedMonths)).toFixed(2))

    // Create installment objects
    for (let i = 0; i < selectedMonths; i++) {
      // Add remainder to the last installment if needed
      const amount = i === selectedMonths - 1 && remainder !== 0
        ? (baseAmount + remainder).toFixed(1)
        : baseAmount.toFixed(1)

      // Assign progressive status based on position
      let progressType: 'empty' | 'small' | 'medium' | 'full' = 'empty'
      if (i === 0) {
        progressType = 'small'
      } else if (i === 1 || (i > 1 && i < selectedMonths - 1)) {
        progressType = 'medium'
      } else if (i === selectedMonths - 1) {
        progressType = 'full'
      }

      installments.push({
        amount: `${amount}`,
        progressType
      })
    }

    return installments
  }

  /**
   * Get installment data as parsed object or generate it dynamically
   */
  const installments = useMemo<Installment[]>(() => {
    return generateInstallments()
  }, [price, selectedMonths])

  /**
   * Format amount
   */
  const formatAmount = (amount: string, label?: string): React.ReactNode => {
    // Format the numeric value
    const numericValue = formatPrice(amount)

    return (
      <div className="installments-amount">
        <div className="amount-container">
          <span className="installments-amount-value">{numericValue}</span>
          {label && <span className="installments-label">{label}</span>}
        </div>
      </div>
    )
  }

  /**
   * Render an installment step
   */
  const renderInstallmentStep = (
    amount: string,
    label: string,
    isFirst: boolean = false,
    isLast: boolean = false,
    progressType: 'empty' | 'small' | 'medium' | 'full' = 'empty'
  ): React.ReactNode => {
    return (
      <div className="installments-step">
        {isFirst
          ? formatAmount(amount, t('Hold'))
          : formatAmount(amount)
        }
        <div className="installments-circle-container">
          {isFirst
            ? (
              <div className="installments-circle-filled-first"></div>
            )
            : isLast || progressType === 'full'
              ? (
                <div className="installments-circle-filled"></div>
              )
              : (
                <>
                  <div className="installments-circle-outline"></div>
                  {progressType === 'small' && (
                    <div className="installments-circle-segment installments-circle-segment-small"></div>
                  )}
                  {progressType === 'medium' && (
                    <div className="installments-circle-segment installments-circle-segment-medium"></div>
                  )}
                </>
              )}
        </div>
        <div className="installments-label">{label}</div>
      </div>
    )
  }

  /**
   * Render a connector between steps
   */
  const renderConnector = (isDotted: boolean = false): React.ReactNode => {
    return (
      <div className="installments-connector-container">
        {isDotted && (
          <div className="installments-dotted-connector">
            <div className="installments-connector-dot"></div>
            <div className="installments-dots-container">
              <div className="installments-dot"></div>
              <div className="installments-dot"></div>
              <div className="installments-dot"></div>
              <div className="installments-dot"></div>
              <div className="installments-dot"></div>
            </div>
            <div className="installments-connector-dot"></div>
          </div>
        )}
      </div>
    )
  }
  const allInstallments = installments
  const installmentsToDisplay: Installment[] = []

  // Handle visibility logic for many installments
  if (allInstallments.length <= maxVisibleInstallments) {
    // If we have fewer installments than max, show all
    installmentsToDisplay.push(...allInstallments)
  } else {
    // If we have more, show first, then ellipsis, then last
    installmentsToDisplay.push(
      allInstallments[0],
      allInstallments[1]
    )
    installmentsToDisplay.push(
      allInstallments[allInstallments.length - 1]
    )
  }

  // Determine if RTL direction is needed based on language
  const isRTL = locale === 'ar'

  return (
    <div
      className='installments payment-progress'
      style={isRTL ? { direction: 'rtl' } : {}}
    >
      {/* Today / Initial Payment */}
      {renderInstallmentStep(
        `${price}`,
        t('Today'),
        true,
        false,
        'empty'
      )}

      {/* Connector between Initial Payment and First Installment */}
      {renderConnector()}

      {/* Display the installments */}
      {installmentsToDisplay.map((installment, index) => {
        // Determine label for installment
        let installmentLabel = ''

        // For first installment, show "After X days" if useFirstInstallmentDays is true
        if (index === 0 && useFirstInstallmentDays) {
          installmentLabel = `${t('After')} ${firstInstallmentDays} ${t('days')}`
        } else {
          // Otherwise, show regular installment number
          installmentLabel = `${t('Installment')} ${index === 2 && allInstallments.length > 3 ? allInstallments.length : index + 1}`
        }

        return (
          <React.Fragment key={index}>
            {renderInstallmentStep(
              installment.amount,
              installmentLabel,
              false,
              index === installmentsToDisplay.length - 1,
              installment.progressType
            )}
            {index < installmentsToDisplay.length - 1 &&
              renderConnector(index === 1 && allInstallments.length > 3)
            }
          </React.Fragment>
        )
      })}
    </div>
  )
}

export default InstallmentTimeline
